<template>
  <div
    class="html-widget"
    ref="htmlContainer"
    :class="widgetClasses"
    :style="widgetStyles"
    v-html="processedHtmlContent"
  ></div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, nextTick, watch } from 'vue'
import type { HtmlWidget } from '@/types/pagebuilder'
import { useWidgetStyles } from '@/composables/useWidgetStyles'
import { processRichTextContent } from '@/utils/textWidgetUtils'
import { parseFacebookEmbeds } from '@/utils/facebookSdk'

interface Props {
  widget: HtmlWidget
}

const props = defineProps<Props>()
const htmlContainer = ref<HTMLElement | null>(null)
const { widgetStyles: baseStyles, widgetClasses } = useWidgetStyles(props.widget)

const textColor = computed(() => props.widget.textColor || '')

const widgetStyles = computed(() => {
  const styles: Record<string, string> = { ...baseStyles.value }
  if (textColor.value) {
    styles.color = textColor.value
    styles['--widget-text-color'] = textColor.value
  }
  return styles
})

const processedHtmlContent = computed(() => {
  return processRichTextContent(props.widget.htmlContent || '', textColor.value || undefined)
})

const executeScripts = async () => {
  if (!htmlContainer.value) return

  const scripts = htmlContainer.value.querySelectorAll('script')

  scripts.forEach((oldScript) => {
    const newScript = document.createElement('script')

    Array.from(oldScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value)
    })

    if (oldScript.textContent) {
      newScript.textContent = oldScript.textContent
    }

    oldScript.parentNode?.replaceChild(newScript, oldScript)
  })

  if (htmlContainer.value.querySelector('.fb-page')) {
    await parseFacebookEmbeds(htmlContainer.value)
  }
}

watch(() => props.widget.htmlContent, () => {
  nextTick(() => {
    executeScripts()
  })
})

onMounted(() => {
  nextTick(() => {
    executeScripts()
  })
})
</script>

<style scoped>
.html-widget {
  line-height: 1.6;
  max-width: 100%;
  box-sizing: border-box;
}

.html-widget :deep(*) {
  max-width: 100%;
}

.html-widget :deep(p),
.html-widget :deep(h1),
.html-widget :deep(h2),
.html-widget :deep(h3),
.html-widget :deep(h4),
.html-widget :deep(h5),
.html-widget :deep(h6),
.html-widget :deep(li),
.html-widget :deep(span),
.html-widget :deep(div),
.html-widget :deep(blockquote),
.html-widget :deep(strong),
.html-widget :deep(em) {
  color: var(--widget-text-color, inherit) !important;
}

.html-widget :deep(img) {
  max-width: 100%;
  height: auto;
}

.html-widget :deep(table) {
  width: 100%;
  border-collapse: collapse;
}

.html-widget :deep(table th),
.html-widget :deep(table td) {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
}
</style>
